/**
 * ExtraRouteVoorstellen — paneel boven de kalender met de voorstellen voor een
 * extra rit (plan 2026-10-28-extra-route-voorstel-op-vraag).
 *
 * De cron route-extra-voorstel schrijft elke ochtend per gebied waar de
 * wachttijd te lang is één voorstel per week. Hier keurt kantoor goed (de rit
 * wordt direct aangemaakt en staat daarna in de klantopties) of slaat over met
 * een reden. Chauffeur en capaciteit zijn vóór het goedkeuren aan te passen.
 * Alleen live-voorstellen; schaduwvoorstellen zijn de ijklijst in key-metrics.
 * Geblokkeerde voorstellen (geen geschikte dag) van de laatste 7 dagen staan
 * erbij zonder knoppen: die rit maakt kantoor met de hand via de plus-knop.
 */

import { useCallback, useEffect, useState } from 'react';
import { useCalendarStore } from '../../stores/calendar-store';
import { salesforceClient } from '../../lib/salesforce';
import type { ExtraVoorstel } from '../../lib/salesforce/types';

const SKIP_REDENEN = ['Geen chauffeur', 'Bus niet vrij', 'Wasserij vol', 'Vraag valt mee', 'Anders'];
const POLL_MS = 300_000;
const WEEK_MS = 7 * 86_400_000;

function datumTekst(iso: string): string {
  return new Date(iso + 'T00:00:00').toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long' });
}

/** Waarom dit voorstel er ligt, in gewone taal (uit de cijfers van de laatste meting). */
function redenTekst(v: ExtraVoorstel): string {
  const c = v.cijfers ?? {};
  const delen: string[] = [];
  const op = c.eerste_vrij_op;
  const af = c.eerste_vrij_af;
  if (op !== undefined || af !== undefined) {
    if (op === af) delen.push(op === null ? 'geen vrije plek binnen 28 dagen' : `eerste vrije plek ${op} dagen weg`);
    else delen.push(`eerste vrije plek ophalen ${op ?? '>28'}, afleveren ${af ?? '>28'} dagen weg`);
  }
  return delen.length > 0 ? delen.join(', ') : (v.reden ?? '');
}

/** Wie er naar deze rit kan: de winbare orders (geen rit, of een rit minstens een week later). */
function VullingRegel({ v }: { v: ExtraVoorstel }) {
  const [open, setOpen] = useState(false);
  const vul = v.cijfers?.vulling;
  if (!vul) return null;
  const rit = (iso: string | null) => (iso ? new Date(iso + 'T00:00:00').toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' }) : 'nog geen rit');
  return (
    <div className="text-xs text-gray-700">
      <button type="button" onClick={() => setOpen(!open)} className="text-left hover:text-gray-900">
        <strong>{vul.totaal} stops</strong> kunnen naar deze rit ({vul.op} ophalen, {vul.af} afleveren)
        <span className="text-indigo-600 ml-1">{open ? 'verberg' : 'welke'}</span>
      </button>
      {open && (
        <ul className="mt-1 max-h-40 overflow-y-auto columns-2 gap-3">
          {vul.orders.map((o) => (
            <li key={`${o.wo}-${o.soort}`} className="truncate">
              {o.naam ?? o.wo} · {o.soort === 'op' ? 'ophalen' : 'afleveren'} · {rit(o.rit)}
            </li>
          ))}
        </ul>
      )}
      <div className="text-gray-500 mt-0.5">Klanten met een latere rit schuiven pas op als we ze de nieuwe datum aanbieden.</div>
    </div>
  );
}

function VoorstelKaart({ voorstel, onKlaar }: { voorstel: ExtraVoorstel; onKlaar: () => void }) {
  const { chauffeurs, fetchRoutes } = useCalendarStore();
  const c = voorstel.cijfers ?? {};
  const vrij = new Map((c.vrije_chauffeurs ?? []).map((k) => [k.sleutel, k]));
  const [chauffeurId, setChauffeurId] = useState(voorstel.chauffeur_sleutel ?? '');
  const [capOp, setCapOp] = useState(String(voorstel.capaciteit_ophalen ?? c.basis_op ?? ''));
  const [capAf, setCapAf] = useState(String(voorstel.capaciteit_afleveren ?? c.basis_af ?? ''));
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState<string | null>(voorstel.fout);
  const [overslaan, setOverslaan] = useState(false);
  const [skipKeuze, setSkipKeuze] = useState(SKIP_REDENEN[0]);
  const [skipTekst, setSkipTekst] = useState('');

  // Lege of basiswaarde = geen override (null), zodat de rit de sjabloon-cap erft.
  const capWaarde = (v: string, basis: number | undefined): number | null => {
    const n = Number(v);
    if (!v.trim() || !Number.isFinite(n) || n === basis) return null;
    return n;
  };

  const goedkeuren = async () => {
    setBezig(true);
    setFout(null);
    try {
      await salesforceClient.extraVoorstelBesluit({
        id: voorstel.id,
        action: 'approve',
        chauffeurId: chauffeurId || null,
        capaciteitOphalen: capWaarde(capOp, c.basis_op),
        capaciteitAfleveren: capWaarde(capAf, c.basis_af),
      });
      await fetchRoutes();
      onKlaar();
    } catch (err) {
      setFout(err instanceof Error ? err.message : 'Goedkeuren mislukt');
    } finally {
      setBezig(false);
    }
  };

  const bevestigOverslaan = async () => {
    const reden = skipKeuze === 'Anders' ? skipTekst.trim() : `${skipKeuze}${skipTekst.trim() ? `: ${skipTekst.trim()}` : ''}`;
    if (reden.length < 3) {
      setFout('Geef een reden op');
      return;
    }
    setBezig(true);
    setFout(null);
    try {
      await salesforceClient.extraVoorstelBesluit({ id: voorstel.id, action: 'skip', reden });
      onKlaar();
    } catch (err) {
      setFout(err instanceof Error ? err.message : 'Overslaan mislukt');
    } finally {
      setBezig(false);
    }
  };

  const veld = 'px-2 py-1.5 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500';

  return (
    <div className="bg-white border border-amber-200 rounded-lg p-3 flex flex-col gap-2 min-w-0">
      <div className="flex items-baseline justify-between gap-2">
        <div className="font-semibold text-gray-900 truncate">{voorstel.master_naam}</div>
        <div className="text-sm text-gray-600 flex-shrink-0">{voorstel.datum ? datumTekst(voorstel.datum) : ''}</div>
      </div>
      <div className="text-xs text-gray-600">{redenTekst(voorstel)}</div>
      <VullingRegel v={voorstel} />
      {c.kandidaat === false && (
        <div className="text-xs text-emerald-700">Volgens de meting van vandaag is deze rit niet meer nodig.</div>
      )}
      {voorstel.status === 'goedgekeurd' && (
        <div className="text-xs text-amber-700">Goedgekeurd, maar de rit is nog niet aangemaakt. Opnieuw goedkeuren probeert het nog een keer.</div>
      )}

      <div className="flex flex-wrap items-end gap-2">
        <label className="flex flex-col text-xs text-gray-600 gap-0.5 flex-1 min-w-[160px]">
          Chauffeur
          <select value={chauffeurId} onChange={(e) => setChauffeurId(e.target.value)} className={veld} disabled={bezig}>
            <option value="">Geen chauffeur</option>
            {chauffeurs.map((ch) => {
              const k = vrij.get(ch.id);
              const label = k ? (k.rooster ? ' (vrij volgens rooster)' : k.dienst ? ` (vrij, ${k.dienst})` : ' (vrij)') : '';
              return (
                <option key={ch.id} value={ch.id}>
                  {ch.name}
                  {label}
                </option>
              );
            })}
          </select>
        </label>
        <label className="flex flex-col text-xs text-gray-600 gap-0.5 w-20">
          Ophalen
          <input type="number" min={1} max={30} value={capOp} onChange={(e) => setCapOp(e.target.value)} className={veld} disabled={bezig} />
        </label>
        <label className="flex flex-col text-xs text-gray-600 gap-0.5 w-20">
          Afleveren
          <input type="number" min={1} max={30} value={capAf} onChange={(e) => setCapAf(e.target.value)} className={veld} disabled={bezig} />
        </label>
      </div>
      {c.accent === 'afleveren' && (
        <div className="text-xs text-gray-500">Meer afleverplekken: het schap staat voller dan de ophaalrij.</div>
      )}

      {fout && <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">{fout}</div>}

      {overslaan ? (
        <div className="flex flex-wrap items-center gap-2">
          <select value={skipKeuze} onChange={(e) => setSkipKeuze(e.target.value)} className={veld} disabled={bezig}>
            {SKIP_REDENEN.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
          <input
            type="text"
            value={skipTekst}
            onChange={(e) => setSkipTekst(e.target.value)}
            placeholder={skipKeuze === 'Anders' ? 'Reden (verplicht)' : 'Toelichting (mag leeg)'}
            className={`${veld} flex-1 min-w-[140px]`}
            disabled={bezig}
          />
          <button type="button" onClick={bevestigOverslaan} disabled={bezig} className="px-3 py-1.5 text-sm font-medium text-white bg-gray-700 rounded-md hover:bg-gray-800 disabled:opacity-50">
            Overslaan
          </button>
          <button type="button" onClick={() => setOverslaan(false)} disabled={bezig} className="px-2 py-1.5 text-sm text-gray-600 hover:text-gray-900">
            Terug
          </button>
        </div>
      ) : (
        <div className="flex gap-2 justify-end">
          <button type="button" onClick={() => setOverslaan(true)} disabled={bezig} className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50">
            Overslaan
          </button>
          <button type="button" onClick={goedkeuren} disabled={bezig} className="px-3 py-1.5 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50">
            {bezig ? 'Bezig...' : 'Goedkeuren'}
          </button>
        </div>
      )}
    </div>
  );
}

export function ExtraRouteVoorstellen() {
  const [concepten, setConcepten] = useState<ExtraVoorstel[]>([]);
  const [geblokkeerd, setGeblokkeerd] = useState<ExtraVoorstel[]>([]);
  const [open, setOpen] = useState(true);

  const laad = useCallback(async () => {
    try {
      // Goedgekeurd = akkoord gegeven maar aanmaken mislukte: blijft als kaart staan om opnieuw te proberen.
      const [c, k, g] = await Promise.all([
        salesforceClient.extraVoorstellen({ status: 'concept', modus: 'live' }),
        salesforceClient.extraVoorstellen({ status: 'goedgekeurd', modus: 'live' }),
        salesforceClient.extraVoorstellen({ status: 'geblokkeerd', modus: 'live' }),
      ]);
      // Flag uit, of niet aangewezen als beoordelaar = paneel weg.
      if (c.modus !== 'live' || c.magBeoordelen === false) {
        setConcepten([]);
        setGeblokkeerd([]);
        return;
      }
      setConcepten([...k.voorstellen, ...c.voorstellen]);
      const grens = Date.now() - WEEK_MS;
      setGeblokkeerd(g.voorstellen.filter((v) => Date.parse(v.created_at) >= grens));
    } catch (err) {
      // Stil: het paneel is een extra, de kalender moet altijd werken.
      console.error('[ExtraRouteVoorstellen] laden faalde:', err);
    }
  }, []);

  useEffect(() => {
    void laad();
    const id = setInterval(() => {
      if (!document.hidden) void laad();
    }, POLL_MS);
    return () => clearInterval(id);
  }, [laad]);

  if (concepten.length === 0 && geblokkeerd.length === 0) return null;

  return (
    <div className="border-b border-amber-200 bg-amber-50">
      <div className="px-4 py-2 flex items-center gap-2">
        <button type="button" onClick={() => setOpen(!open)} className="flex items-center gap-2 text-sm font-medium text-amber-900">
          <svg viewBox="0 0 20 20" fill="currentColor" className={`w-4 h-4 transition-transform ${open ? 'rotate-90' : ''}`}>
            <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
          </svg>
          Extra rit voorgesteld ({concepten.length})
        </button>
        <span className="text-xs text-amber-800">gebieden waar de klant te lang op een plek wacht</span>
      </div>
      {open && (
        <div className="px-4 pb-3 flex flex-col gap-2">
          {concepten.length > 0 && (
            <div className="grid gap-2 grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
              {concepten.map((v) => (
                <VoorstelKaart key={v.id} voorstel={v} onKlaar={() => void laad()} />
              ))}
            </div>
          )}
          {geblokkeerd.map((v) => (
            <div key={v.id} className="text-xs text-amber-900 bg-white/60 border border-amber-200 rounded-md px-3 py-2">
              <strong>{v.master_naam}</strong>: geen voorstel mogelijk ({v.reden}). Maak de rit met de hand via de
              plus-knop op de gewenste dag.
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
